// src/components/KeyValuesDashboard/components/tabs/DailyTab.tsx

import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp } from 'lucide-react';

import type { ComparisonResponse } from '@/types/analytics';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDate,
  getMetricTotal,
  generateColors,
} from '@/utils/keyvalues-helpers';

interface DailyTabProps {
  data: ComparisonResponse;
  mode: 'all' | 'compare';
}

export function DailyTab({ data }: DailyTabProps) {
  const [selectedMetric, setSelectedMetric] = useState<'revenue' | 'impressions' | 'clicks' | 'ctr'>('revenue');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const dailyData = data.daily_breakdown?.daily_data || [];
  const allValues = data.selected_values;

  // Se nenhum valor selecionado, seleciona os primeiros 5
  const valuesToShow = selectedValues.length > 0 
    ? selectedValues 
    : allValues.slice(0, 5);

  // Preparar dados para o gráfico
  const chartData = dailyData.map(day => {
    const dataPoint: any = {
      date: formatDate(day.date),
      fullDate: day.date,
    };

    valuesToShow.forEach(valueName => {
      const valueData = day.values.find(v => v.custom_value === valueName);
      if (valueData) {
        dataPoint[valueName] = getMetricTotal(valueData.metrics, selectedMetric);
      } else {
        dataPoint[valueName] = 0;
      }
    });

    return dataPoint;
  });

  const colors = generateColors(valuesToShow.length);

  const formatYAxis = (value: number) => {
    switch (selectedMetric) {
      case 'revenue':
        return `$${(value / 1000).toFixed(0)}k`;
      case 'impressions':
      case 'clicks':
        return `${(value / 1000).toFixed(0)}k`;
      case 'ctr':
        return `${value.toFixed(1)}%`;
      default:
        return value.toString();
    }
  };

  const formatTooltipValue = (value: number) => {
    switch (selectedMetric) {
      case 'revenue':
        return formatCurrency(value);
      case 'impressions':
      case 'clicks':
        return formatNumber(value);
      case 'ctr':
        return formatPercentage(value);
      default:
        return value.toString();
    }
  };

  const handleValueToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter(v => v !== value));
    } else {
      setSelectedValues([...selectedValues, value]);
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Controles */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Evolução Diária
          </h3>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Métrica:</span>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value as any)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="revenue">Receita</option>
                <option value="impressions">Impressões</option>
                <option value="clicks">Cliques</option>
                <option value="ctr">CTR</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Seletor de Valores */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="mb-4">
          <p className="text-sm font-medium text-slate-700 mb-3">
            Selecione os valores para visualizar no gráfico:
            {selectedValues.length > 0 && (
              <span className="ml-2 text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                {selectedValues.length} selecionados
              </span>
            )}
          </p>
          
          {selectedValues.length > 0 && (
            <button
              onClick={() => setSelectedValues([])}
              className="text-xs text-slate-600 hover:text-rose-600 underline mb-3"
            >
              Limpar seleção (mostrar top 5)
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {allValues.map((value) => {
            const isSelected = selectedValues.includes(value) || 
              (selectedValues.length === 0 && valuesToShow.includes(value));
            
            return (
              <button
                key={value}
                onClick={() => handleValueToggle(value)}
                className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-indigo-500 bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-900 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
                }`}
                title={value}
              >
                <span className="truncate block">{value}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gráfico de Linhas */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: '#64748b' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickFormatter={formatYAxis}
            />
            <RechartsTooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-4 rounded-xl shadow-xl border border-slate-200 min-w-[200px]">
                      <p className="font-semibold text-slate-900 mb-3">{label}</p>
                      <div className="space-y-2">
                        {payload.map((entry: any) => (
                          <div key={entry.name} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-sm text-slate-600 truncate max-w-[120px]">
                                {entry.name}
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-slate-900">
                              {formatTooltipValue(entry.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
            
            {valuesToShow.map((value, index) => (
              <Line
                key={value}
                type="monotone"
                dataKey={value}
                stroke={colors[index]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Estatísticas Diárias */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Estatísticas por Dia
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-50 z-10">
                  Data
                </th>
                {valuesToShow.map((value) => (
                  <th key={value} className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {value.length > 15 ? value.substring(0, 15) + '...' : value}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {dailyData.map((day) => (
                <tr key={day.date} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                    {day.date}
                  </td>
                  {valuesToShow.map((valueName) => {
                    const valueData = day.values.find(v => v.custom_value === valueName);
                    const metricValue = valueData ? getMetricTotal(valueData.metrics, selectedMetric) : 0;
                    
                    return (
                      <td key={valueName} className="px-6 py-4 whitespace-nowrap text-right text-slate-600">
                        {formatTooltipValue(metricValue)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumo do Período */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {valuesToShow.slice(0, 4).map((value, index) => {
          const totalRevenue = dailyData.reduce((sum, day) => {
            const valueData = day.values.find(v => v.custom_value === value);
            return sum + (valueData ? getMetricTotal(valueData.metrics, 'revenue') : 0);
          }, 0);

          return (
            <div key={value} className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200/50 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colors[index] }}
                />
                <p className="text-sm font-medium text-slate-700 truncate">
                  {value}
                </p>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(totalRevenue)}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Receita Total no Período
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
